import { useCallback, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommand, enqueueWithDraft, getDraft } from '@/offline'
import type { PackLot } from '@/offline/sitePack'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  Keypad,
  Readout,
  Row,
  SpeakButton,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
} from '@/features/gate/v1/kit'
import { LiveScanner, Refused, TypeCodeSheet } from '@/features/gate/v1/scan'
import { C, typeKey } from '@/features/gate/v1/style'
import { fmtTime, kg, plain, rosterShort, shortCode, shortLot, tr } from '@/features/gate/v1/text'
import { RefusedCard } from '@/features/store/v1/bits'
import type { RecordOutputPayload, WeighInPayload } from '@/features/process/commands'
import {
  isRunLive,
  runParentIds,
  type PackRunView,
  type ProcessState,
} from '@/features/process/projection'
import { unknownInputWeights } from '@/features/process/model/rules'
import { CallSheet, SavedLine, TagChip } from '@/features/process/v1/ui'
import { freshCodeV1 } from '@/features/process/v1/model'
import { PScanner, refusalText } from '@/features/process/v1/parts'
import { useDraftState } from '@/features/process/useProcess'
import type { PackClosePayload, VerifyPouchPayload } from '../commands'
import '../commands'
import { KIcon } from './icons'
import { DelabelRows } from './Delabel'
import { HallTile, HeldRunsTile, LotsTile } from './Lots'
import {
  addPouches,
  cartons,
  checkPouchV1,
  checkSackV1,
  codesPrinted,
  coderLines,
  diffTail,
  emptyRunDraft,
  fillPct,
  inputGrams,
  isStarted,
  packOverfill,
  nextJob,
  runHold,
  runJobs,
  runSacks,
  sackGrams,
  setPouches,
  typedPouch,
  undoPouches,
  withSiteRun,
  type RunDraft,
  type RunStage,
} from './model'
import {
  BigButton,
  CoderBoxes,
  DoneScreen,
  GoodBand,
  IconRows,
  JobCircle,
  Lead,
  Locked,
  Note,
  PackHeader,
  Steps,
  WithHall,
  YesSheet,
} from './ui'
import { ki, lotHeader, packSize, productName, type HeaderLot } from './words'

const saveFailed = (e: unknown) =>
  `${tr('process.saveFailed')}${e instanceof Error && e.message ? ` · ${e.message}` : ''}`

/** The line's last close reading, kept on this phone (the coder counter is never zeroed). */
const counterDraftId = (lineId: string | null | undefined) => `pack.v1.counter:${lineId ?? 'line'}`

/** V1-F7-02i … 04j — one pack run on the line, from keying the coder to the close. */
export function V1PackRun() {
  const { runId = '' } = useParams()
  const navigate = useNavigate()
  return (
    <WithHall>
      {(state) => {
        const pr = state.packRuns.get(runId)
        if (!pr)
          return (
            <V1Screen
              nav
              header={
                <PackHeader
                  onBack={() => navigate('/m/pack')}
                  icon="pouch"
                  title={tr('pack.v1.run.missing')}
                />
              }
            >
              <Strip icon="bang">{tr('pack.v1.run.missingBody')}</Strip>
            </V1Screen>
          )
        return <Run key={pr.id} state={state} run={withSiteRun(state, pr)} />
      }}
    </WithHall>
  )
}

function Run({ state, run }: { state: ProcessState; run: PackRunView }) {
  const actor = useActor()
  const [draft, update, loaded] = useDraftState<RunDraft>(
    `pack.v1.run:${run.id}`,
    'pack.v1.run',
    emptyRunDraft,
    actor.user_id,
  )
  if (!loaded) return <V1Screen />
  const lot = run.pack_lot ? (state.packLots.get(run.pack_lot.id) ?? run.pack_lot) : null
  const closed =
    draft.closed ??
    (run.close
      ? {
          pouches: run.close.pouches_good,
          leftover_g: 0,
          reading: 0,
          lot_code: lot?.code ?? run.code,
        }
      : null)
  if (closed) return <Closed run={run} closed={closed} />
  if (!isRunLive(run.status) || !isStarted(run) || !lot) return <Navigate to="/m/pack" replace />

  const p: RunProps = {
    state,
    run,
    lot,
    draft,
    update,
    head: lotHeader(run, lot.code),
    go: (stage: RunStage) => update((d) => ({ ...d, stage })),
  }
  const hold = runHold(state, run)
  if (hold.held && !draft.stop_ack) return <StopRun {...p} lab={hold.lab} />
  if (!draft.keyed) return <KeyCoder {...p} />
  switch (draft.stage) {
    case 'sacks':
      return <SackScan {...p} />
    case 'first':
      return <FirstPouch {...p} />
    case 'fill':
      return <Filling {...p} />
    case 'leftover':
      return <Leftover {...p} />
    case 'counter':
      return <Counter {...p} />
    default:
      return <RunHome {...p} />
  }
}

interface RunProps {
  state: ProcessState
  run: PackRunView
  lot: PackLot
  draft: RunDraft
  update: (fn: (d: RunDraft) => RunDraft) => void
  head: HeaderLot
  go: (stage: RunStage) => void
}

// ---------------------------------------------------------------------------------------------
// F7.2 — कोडर में डालें
// ---------------------------------------------------------------------------------------------

function KeyCoder({ run, lot, update, head }: RunProps) {
  const c = coderLines(lot)
  return (
    <V1Screen
      header={<PackHeader lot={head} icon="printer" title={tr('pack.v1.coder.title')} />}
      footer={
        <Footer>
          <Row>
            <Btn icon={ki('printer')} onClick={() => window.print()}>
              {tr('pack.v1.coder.sheet')}
            </Btn>
          </Row>
          <Cta
            label={tr('pack.v1.coder.done')}
            icon="next"
            onClick={() => update((d) => ({ ...d, keyed: true }))}
          />
        </Footer>
      }
    >
      {run.started_at && (
        <SavedLine>{tr('pack.v1.coder.started', { time: fmtTime(run.started_at) })}</SavedLine>
      )}
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <CoderBoxes label={tr('pack.v1.coder.lot')} boxes={c.lot} size={30} />
        <CoderBoxes label={tr('pack.v1.coder.packed')} boxes={c.packed} size={26} />
        <CoderBoxes label={tr('pack.v1.coder.bb')} boxes={c.bestBefore} size={26} />
        <CoderBoxes label={tr('pack.v1.coder.qr')} boxes={c.qr} size={18} />
      </section>
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.3 — पैकिंग चालू: four jobs, then close
// ---------------------------------------------------------------------------------------------

function RunHome({ state, run, draft, head, go }: RunProps) {
  const jobs = runJobs(run, draft)
  const next = nextJob(jobs)
  const { total, g: totalG } = runSacks(state, run)
  const cta: Record<number, { label: string; icon: Parameters<typeof ki>[0]; stage: RunStage }> = {
    1: { label: tr('pack.v1.run.job1'), icon: 'hopper', stage: 'sacks' },
    2: { label: tr('pack.v1.run.job2'), icon: 'qr', stage: 'first' },
    3: { label: tr('pack.v1.run.job3'), icon: 'pouch', stage: 'fill' },
    4: { label: tr('pack.v1.run.job4'), icon: 'leftover', stage: 'leftover' },
    5: { label: tr('pack.v1.close.cta'), icon: 'stamp', stage: 'counter' },
  }
  const now = cta[next]!
  return (
    <V1Screen
      nav
      header={<PackHeader lot={head} icon="pouch" title={tr('pack.v1.run.title')} />}
      footer={
        <Footer>
          <Cta label={now.label} icon={ki(now.icon)} onClick={() => go(now.stage)} />
        </Footer>
      }
    >
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '8px 12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 16px',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.acc }}>
          <Icon name="sack" size={22} />
          <b style={{ fontSize: 17, color: C.ink }}>
            {tr('pack.v1.home.batch', { sacks: total, kg: kg(totalG) })}
          </b>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.acc }}>
          <KIcon name="pouch" size={22} />
          <b style={{ fontSize: 17, color: C.ink }}>
            {tr('pack.v1.run.about', { pouches: run.planned_pouches })}
          </b>
        </span>
      </section>
      <div
        style={{
          borderRadius: 14,
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          padding: '6px 10px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4 }}>
          <JobCircle
            n={1}
            icon="hopper"
            label={tr('pack.v1.run.job1')}
            done={jobs.sacks}
            current={next === 1}
            badge={run.inputs.length}
            onClick={() => go('sacks')}
          />
          <JobCircle
            n={2}
            icon="qr"
            label={tr('pack.v1.run.job2')}
            done={jobs.first}
            current={next === 2}
            onClick={() => go('first')}
          />
          <JobCircle
            n={3}
            icon="pouch"
            label={tr('pack.v1.run.job3')}
            done={jobs.fill}
            current={next === 3}
            badge={draft.count}
            onClick={() => go('fill')}
          />
          <JobCircle
            n={4}
            icon="leftover"
            label={tr('pack.v1.run.job4')}
            done={jobs.leftover}
            current={next === 4}
            onClick={() => go('leftover')}
          />
        </div>
        <div style={{ borderTop: `1px solid ${C.rule}` }} />
        <button
          type="button"
          onClick={() => next === 5 && go('counter')}
          style={{
            border: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '4px 0',
            textAlign: 'left',
            fontFamily: "'Mukta', sans-serif",
            color: C.ink,
            cursor: next === 5 ? 'pointer' : 'default',
          }}
        >
          <span
            style={{
              position: 'relative',
              width: 56,
              height: 56,
              borderRadius: 28,
              background: C.sunk,
              color: C.acc,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <KIcon name="stamp" size={30} />
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: -2,
                top: -2,
                width: 24,
                height: 24,
                borderRadius: 12,
                background: C.ink,
                color: '#ffffff',
                fontSize: 14,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              5
            </span>
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <b style={{ fontSize: 18 }}>{tr('pack.v1.run.then')}</b>
            <span style={{ fontSize: 15, color: C.muted }}>{tr('pack.v1.run.thenLine')}</span>
          </span>
        </button>
      </div>
      {/* The pack home is skipped while this run fills: its held runs, delabel runs, lots and hall. */}
      <HeldRunsTile state={state} />
      <DelabelRows state={state} />
      <LotsTile />
      <HallTile />
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.3 — बोरी स्कैन करें (the scan is the issue; kilos from the sack's record)
// ---------------------------------------------------------------------------------------------

function SackScan({ state, run, head, go }: RunProps) {
  const actor = useActor()
  const [saved, setSaved] = useState<{ n: number; g: number } | null>(null)
  const [wrong, setWrong] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [call, setCall] = useState(false)
  const count = run.inputs.length
  const { total } = runSacks(state, run)
  // Sacks saved from this screen: the camera may read a tag again before the list shows it in.
  const tipped = useRef(new Set<string>())

  const tip = async (code: string, entry: 'SCAN' | 'TYPED') => {
    const v = checkSackV1(state, run, code)
    if (!v.ok) return
    tipped.current.add(v.value.container_id)
    try {
      const id = newId()
      // V1: no scale at the hopper and no issue step — the sack's recorded kilos go in.
      const payload: WeighInPayload = {
        run_input_id: id,
        run_id: run.id,
        qr_payload: code,
        entry,
        container_id: v.value.container_id,
        weight_read_g: null,
        issue_seen: false,
        confirmed_twice: false,
      }
      await enqueueCommand({
        id,
        type: 'weigh_in',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      setSaved({ n: count + 1, g: sackGrams(v.value) })
    } catch (e) {
      tipped.current.delete(v.value.container_id)
      setErr(saveFailed(e))
    }
  }

  const onCode = (code: string, entry: 'SCAN' | 'TYPED') => {
    setErr(null)
    const v = checkSackV1(state, run, code)
    if (v.ok && tipped.current.has(v.value.container_id)) return refusalText('DUPLICATE')
    if (v.ok) {
      void tip(code, entry)
      return null
    }
    if (v.reason === 'NOT_THIS_BATCH') {
      setWrong(code)
      return null
    }
    return v.reason === 'NOT_A_SACK' ? tr('pack.v1.sack.notSack') : refusalText(v.reason)
  }

  if (wrong) {
    const bag = state.bagByPayload.get(wrong.trim().toUpperCase())
    const unit = bag ? state.units.get(bag.unit_id) : undefined
    const farmer =
      unit?.farmer_code ?? (typeof bag?.farmer_code === 'string' ? bag.farmer_code : null)
    const batch = unit?.code ? tr('pack.v1.batchTail', { tail: shortLot(unit.code) }) : ''
    return (
      <V1Screen
        header={
          <PackHeader
            onBack={() => setWrong(null)}
            lot={head}
            icon="hopper"
            title={tr('pack.v1.sack.title')}
            step={1}
          />
        }
        footer={
          <Footer>
            <Row>
              <Btn icon="call" onClick={() => setCall(true)}>
                {tr('pack.v1.supervisor')}
              </Btn>
            </Row>
            <Cta label={tr('pack.v1.gotIt')} icon="ok" onClick={() => setWrong(null)} />
          </Footer>
        }
        overlay={
          call ? (
            <CallSheet
              state={state}
              title={tr('pack.v1.supervisor')}
              onClose={() => setCall(false)}
            />
          ) : undefined
        }
      >
        <RefusedCard
          title={tr('pack.v1.wrong.title')}
          why={[tr('process.v1.tag', { no: shortCode(wrong) }), batch].filter(Boolean).join(' · ')}
          steps={[tr('pack.v1.wrong.s1'), tr('pack.v1.wrong.s2'), tr('pack.v1.wrong.s3')]}
        />
        {(farmer || batch) && (
          <ItemRow
            padding="8px 10px"
            lead={
              <span
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  background: C.photo,
                  color: C.photoInk,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Icon name="user" size={32} sw={1.6} />
              </span>
            }
            title={farmer ? tr('pack.v1.farmerNo', { no: rosterShort(farmer) }) : batch}
            line={farmer ? batch : undefined}
          />
        )}
      </V1Screen>
    )
  }

  return (
    <V1Screen
      header={
        <PackHeader
          onBack={() => go('home')}
          lot={head}
          icon="hopper"
          title={tr('pack.v1.sack.title')}
          step={1}
        />
      }
      band={
        <GoodBand>{tr('pack.v1.sack.band', { n: count, total, kg: kg(inputGrams(run)) })}</GoodBand>
      }
      footer={
        <Footer>
          {err && <Refused text={err} />}
          {count > 0 ? (
            <Cta label={tr('pack.v1.sack.allIn')} icon="next" onClick={() => go('home')} />
          ) : (
            <WaitCta>{tr('pack.v1.sack.wait')}</WaitCta>
          )}
        </Footer>
      }
    >
      {saved && <SavedLine>{tr('pack.v1.sack.saved', { n: saved.n, kg: kg(saved.g) })}</SavedLine>}
      <PScanner
        state={state}
        kind="BAG"
        icon="tag"
        hint={tr('pack.v1.sack.hint')}
        onCode={onCode}
      />
      <Note icon="hopper">{tr('pack.v1.sack.then')}</Note>
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.3 — पहला पाउच: the QR against the lot's link (the only pouch check in V1)
// ---------------------------------------------------------------------------------------------

type PouchView = { kind: 'scan' } | { kind: 'wont' } | { kind: 'wrong'; got: string }

function FirstPouch({ state, run, lot, update, head, go }: RunProps) {
  const actor = useActor()
  const [view, setView] = useState<PouchView>({ kind: 'scan' })
  const [typing, setTyping] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [call, setCall] = useState(false)

  const record = async (text: string, match: boolean) => {
    const payload: VerifyPouchPayload = {
      run_id: run.id,
      pack_lot_id: lot.id,
      position: 'FIRST',
      scanned_text: text,
      match,
      photo_id: null,
    }
    await enqueueCommand({
      type: 'verify_pouch',
      payload,
      actorId: actor.user_id,
      parentIds: runParentIds(run),
    })
  }

  // One pouch check at a time (the camera may read the same QR again while it is saved).
  const busy = useRef(false)
  const decide = (v: ReturnType<typeof checkPouchV1> | null): string | null => {
    if (!v || busy.current) return null
    if (v.kind === 'SACK_TAG') return tr('pack.v1.first.sackTag')
    busy.current = true
    void record(v.text, v.kind === 'MATCH')
      .finally(() => {
        busy.current = false
      })
      .then(() => {
        if (v.kind === 'MATCH') update((d) => ({ ...d, first_ok: true, stage: 'fill' }))
        else setView({ kind: 'wrong', got: v.lot ?? v.text })
      })
      .catch((e: unknown) => setErr(saveFailed(e)))
    return null
  }

  const supervisor = call ? (
    <CallSheet state={state} title={tr('pack.v1.supervisor')} onClose={() => setCall(false)} />
  ) : undefined

  if (view.kind === 'wont')
    return (
      <V1Screen
        header={
          <PackHeader
            onBack={() => setView({ kind: 'scan' })}
            lot={head}
            icon="qr"
            title={tr('pack.v1.first.title')}
            step={2}
          />
        }
        footer={
          <Footer>
            <Row>
              <Btn icon="call" onClick={() => setCall(true)}>
                {tr('pack.v1.supervisor')}
              </Btn>
            </Row>
            <Cta
              label={tr('pack.v1.first.again')}
              icon={ki('qr')}
              onClick={() => setView({ kind: 'scan' })}
            />
          </Footer>
        }
        overlay={supervisor}
      >
        <RefusedCard
          title={tr('pack.v1.wont.title')}
          why={tr('pack.v1.wont.why')}
          steps={[tr('pack.v1.wont.s1'), tr('pack.v1.wont.s2'), tr('pack.v1.wont.s3')]}
        />
        <Note icon="hopper">{tr('pack.v1.wont.cut')}</Note>
      </V1Screen>
    )

  if (view.kind === 'wrong') {
    const d = diffTail(view.got.toUpperCase(), lot.code)
    return (
      <V1Screen
        header={
          <PackHeader
            onBack={() => setView({ kind: 'scan' })}
            lot={head}
            icon="qr"
            title={tr('pack.v1.first.title')}
            step={2}
          />
        }
        footer={
          <Footer>
            <Row>
              <Btn icon={ki('file')} onClick={() => update((x) => ({ ...x, keyed: false }))}>
                {tr('pack.v1.coder.sheet')}
              </Btn>
            </Row>
            <Cta
              label={tr('pack.v1.first.again')}
              icon={ki('qr')}
              onClick={() => setView({ kind: 'scan' })}
            />
          </Footer>
        }
      >
        <RefusedCard
          title={tr('pack.v1.mismatch.title')}
          why=""
          steps={[tr('pack.v1.wont.s1'), tr('pack.v1.mismatch.s2'), tr('pack.v1.wont.s3')]}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <LotBox ok={false} label={tr('pack.v1.mismatch.got')} same={d.same} rest={d.got} />
          <LotBox ok label={tr('pack.v1.mismatch.want')} same={d.same} rest={d.want} />
        </div>
        <Note icon="hopper">{tr('pack.v1.mismatch.cut')}</Note>
      </V1Screen>
    )
  }

  return (
    <V1Screen
      header={
        <PackHeader
          onBack={() => go('home')}
          lot={head}
          icon="qr"
          title={tr('pack.v1.first.title')}
          step={2}
        />
      }
      band={<GoodBand>{tr('pack.v1.goodLot', { tail: shortLot(lot.code) })}</GoodBand>}
      footer={
        <Footer>
          {err && <Refused text={err} />}
          <WaitCta>{tr('pack.v1.first.wait')}</WaitCta>
        </Footer>
      }
    >
      {typing && (
        <TypeCodeSheet
          title={tr('pack.v1.first.type')}
          check={(raw) => {
            setErr(null)
            return decide(typedPouch(lot, raw))
          }}
          onClose={() => setTyping(false)}
        />
      )}
      <Note icon="printer">{tr('pack.v1.first.print')}</Note>
      {run.inputs.length === 0 && <Strip icon="bang">{tr('pack.v1.first.noSack')}</Strip>}
      {!typing && (
        <LiveScanner
          icon={ki('qr')}
          hint={tr('pack.v1.first.hint')}
          minHeight={160}
          check={(raw) => decide(checkPouchV1(state, lot, raw))}
          onError={setErr}
        />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn icon="keypad" onClick={() => setTyping(true)}>
          {tr('gate.v1.tag.type')}
        </Btn>
        <Btn icon="bang" onClick={() => setView({ kind: 'wont' })}>
          {tr('pack.v1.first.wont')}
        </Btn>
      </div>
    </V1Screen>
  )
}

/** "पाउच पर …01 M" / "चाहिए …01 H". */
function LotBox({
  ok,
  label,
  same,
  rest,
}: {
  ok: boolean
  label: string
  same: string
  rest: string
}) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: `2px solid ${ok ? C.ok : C.bad}`,
        background: C.surface,
        padding: '8px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: ok ? 13 : 4,
          background: ok ? C.ok : C.bad,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={ok ? 'ok' : 'x'} size={16} sw={3} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.15 }}>
        <span style={{ fontSize: 15, color: C.muted }}>{label}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 18, color: C.muted }}>…{same}</span>
          <b
            style={{
              fontSize: 30,
              color: ok ? C.ok : C.bad,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {rest}
          </b>
        </span>
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.3 — पाउच भरें: the count, a carton at a time
// ---------------------------------------------------------------------------------------------

function Filling({ run, lot, draft, update, head, go }: RunProps) {
  const per = run.sku.pouches_per_carton
  const [saved, setSaved] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const c = cartons(draft.count, per)
  const pct = fillPct(draft.count, run.planned_pouches)
  return (
    <V1Screen
      header={
        <PackHeader
          onBack={() => go('home')}
          lot={head}
          icon="pouch"
          title={tr('pack.v1.fill.title')}
          step={3}
        />
      }
      band={<GoodBand>{tr('pack.v1.goodLot', { tail: shortLot(lot.code) })}</GoodBand>}
      footer={
        <Footer>
          <Row>
            <Btn icon="keypad" onClick={() => setTyping(true)}>
              {tr('pack.v1.fill.type')}
            </Btn>
            <Btn
              icon="pencil"
              disabled={draft.adds.length === 0}
              onClick={() => (setSaved(null), update(undoPouches))}
            >
              {tr('pack.v1.fill.undo')}
            </Btn>
          </Row>
          {draft.count > 0 ? (
            <Cta
              label={tr('pack.v1.fill.done')}
              icon={ki('pouch')}
              onClick={() => go('leftover')}
            />
          ) : (
            <WaitCta>{tr('pack.v1.fill.wait')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        typing ? (
          <CountSheet
            value={draft.count}
            onClose={() => setTyping(false)}
            onSave={(n) => {
              update((d) => setPouches(d, n))
              setSaved(null)
              setTyping(false)
            }}
          />
        ) : undefined
      }
    >
      {saved && <SavedLine>{saved}</SavedLine>}
      <section
        style={{
          background: C.surface,
          border: `2px solid ${C.acc}`,
          borderRadius: 12,
          padding: '8px 12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <span style={{ fontSize: 16, color: C.muted, fontWeight: 600, lineHeight: 1.3 }}>
              {tr('pack.v1.fill.filled')}
            </span>
            <b style={{ fontSize: 60 }}>{draft.count}</b>
          </span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              lineHeight: 1.2,
            }}
          >
            <b style={{ fontSize: 24 }}>{tr('pack.v1.cartons', { count: c.full })}</b>
            <span style={{ fontSize: 15, color: C.muted }}>
              {tr('pack.v1.run.about', { pouches: run.planned_pouches })}
            </span>
          </span>
        </div>
        <div style={{ height: 10, borderRadius: 6, background: C.rule, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: C.acc }} />
        </div>
      </section>
      <BigButton
        icon="carton"
        title={tr('pack.v1.fill.carton')}
        line={tr('pack.v1.pouches', { count: per })}
        onClick={() => {
          update((d) => addPouches(d, per))
          const n = draft.count + per
          setSaved(tr('pack.v1.fill.saved', { cartons: Math.floor(n / per), pouches: n }))
        }}
      />
      <ItemRow
        padding="8px 10px"
        lead={<Lead icon="hopper" />}
        title={<span style={{ fontSize: 18 }}>{tr('pack.v1.fill.moreSacks')}</span>}
        right={
          <div style={{ flex: '0 0 auto', display: 'flex' }}>
            <Btn icon="scan" onClick={() => go('sacks')}>
              {tr('pack.v1.scan')}
            </Btn>
          </div>
        }
      />
    </V1Screen>
  )
}

/** गिनती लिखें: the pouches counted by hand. */
function CountSheet({
  value,
  onClose,
  onSave,
}: {
  value: number
  onClose: () => void
  onSave: (n: number) => void
}) {
  const [v, setV] = useState(value ? String(value) : '')
  const onKey = useCallback(
    (k: string) => setV((cur) => typeKey(cur, k, { decimals: 0, max: 6 })),
    [],
  )
  return (
    <V1Sheet
      title={tr('pack.v1.fill.typeTitle')}
      onClose={onClose}
      top={120}
      actions={
        v ? (
          <Cta label={tr('pack.v1.ok')} icon="ok" onClick={() => onSave(Number(v))} />
        ) : (
          <WaitCta>{tr('pack.v1.fill.type')}</WaitCta>
        )
      }
    >
      <Readout
        label={tr('pack.v1.fill.filled')}
        value={v}
        unit={tr('pack.v1.pouchUnit')}
        size={36}
      />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.3 — बचा माल: the hopper leftover into a new tagged sack, weighed
// ---------------------------------------------------------------------------------------------

function Leftover({ state, run, draft, update, head, go }: RunProps) {
  const actor = useActor()
  const [value, setValue] = useState(draft.leftover ? (draft.leftover.g / 1000).toString() : '')
  const [tag, setTag] = useState<{ code: string; entry: 'SCAN' | 'TYPED' } | null>(
    draft.leftover?.tag ? { code: draft.leftover.tag, entry: 'SCAN' } : null,
  )
  const [scanning, setScanning] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const onKey = useCallback((k: string) => {
    setErr(null)
    setValue((c) => typeKey(c, k, { decimals: 2, max: 6 }))
  }, [])
  const sack =
    state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0] ?? null
  const grams = value ? Math.round(Number(value) * 1000) : 0
  const none = value !== '' && grams === 0
  // Already saved on this run: a second save would make a second sack.
  const savedBefore = !!draft.leftover && draft.leftover.g > 0

  const save = async () => {
    if (none) {
      update((d) => ({ ...d, leftover: { g: 0, tag: null }, stage: 'counter' }))
      return
    }
    if (savedBefore) {
      update((d) => ({ ...d, stage: 'counter' }))
      return
    }
    if (!tag || !sack) return
    const net = grams - sack.tare_g
    if (net <= 0) {
      setErr(tr('process.v1.weigh.tooLight'))
      return
    }
    setSaving(true)
    setErr(null)
    try {
      const container_id = newId()
      const payload: RecordOutputPayload = {
        container_id,
        unit_id: newId(),
        run_id: run.id,
        qr_payload: tag.code,
        entry: tag.entry,
        stream_key: 'LEFTOVER',
        grade: null,
        sack_type_id: sack.id,
        tare_g: sack.tare_g,
        weight_read_g: grams,
        confirmed_twice: false,
        moisture_pct: null,
        tie: 'NONE',
      }
      await enqueueCommand({
        id: container_id,
        type: 'record_output',
        payload,
        actorId: actor.user_id,
        parentIds: runParentIds(run),
      })
      update((d) => ({ ...d, leftover: { g: net, tag: tag.code }, stage: 'counter' }))
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  const ready = none || savedBefore || (grams > 0 && !!tag && !!sack)
  return (
    <V1Screen
      main={{ padding: '8px 12px', gap: 8 }}
      header={
        <PackHeader
          onBack={() => go('fill')}
          lot={head}
          icon="leftover"
          title={tr('pack.v1.left.title')}
          step={4}
        />
      }
      band={<GoodBand>{tr('pack.v1.left.band')}</GoodBand>}
      footer={
        <Footer dense>
          {err && <Refused text={err} />}
          {ready ? (
            <Cta label={tr('pack.v1.save')} icon="ok" busy={saving} onClick={() => void save()} />
          ) : (
            <WaitCta>
              {grams > 0 && !tag
                ? tr('pack.v1.left.needTag')
                : !sack && grams > 0
                  ? tr('pack.v1.left.noSack')
                  : tr('pack.v1.left.wait')}
            </WaitCta>
          )}
        </Footer>
      }
      overlay={
        scanning ? (
          <V1Sheet title={tr('pack.v1.left.tag')} onClose={() => setScanning(false)} top={60}>
            <PScanner
              state={state}
              kind="OUTPUT_BAG"
              hint={tr('pack.v1.left.tag')}
              onCode={(code, entry) => {
                const v = freshCodeV1(state, code, 'OUTPUT_BAG', actor.user_id)
                if (!v.ok) return refusalText(v.reason)
                setTag({ code: v.value, entry })
                setScanning(false)
                return null
              }}
            />
          </V1Sheet>
        ) : undefined
      }
    >
      <Readout
        label={tr('pack.v1.left.label')}
        value={value}
        unit={tr('process.v1.kgUnit')}
        size={44}
        padding="4px 12px 6px"
        labelSize={16}
        speakText={value ? plain(tr('pack.v1.left.speak', { kg: value })) : ''}
      />
      {savedBefore ? (
        <SavedLine>{tr('pack.v1.left.saved', { kg: kg(draft.leftover!.g) })}</SavedLine>
      ) : tag ? (
        <TagChip code={tag.code} onBad={() => setTag(null)} />
      ) : (
        <ItemRow
          padding="6px 10px"
          lead={
            <span style={{ color: C.acc, display: 'flex' }}>
              <Icon name="tag" size={26} />
            </span>
          }
          title={<span style={{ fontSize: 18 }}>{tr('pack.v1.left.tag')}</span>}
          right={
            <div style={{ flex: '0 0 auto', display: 'flex' }}>
              <Btn icon="scan" disabled={none} onClick={() => setScanning(true)}>
                {tr('pack.v1.scan')}
              </Btn>
            </div>
          }
        />
      )}
      <div style={{ flexGrow: 1 }} />
      <Keypad onKey={onKey} />
    </V1Screen>
  )
}

// ---------------------------------------------------------------------------------------------
// F7.4 — कोडर का काउंटर, रन बंद करें?
// ---------------------------------------------------------------------------------------------

function Counter({ state, run, lot, draft, update, head, go }: RunProps) {
  const actor = useActor()
  const [value, setValue] = useState('')
  const [sheet, setSheet] = useState(false)
  // Codes the coder printed that never went on a good pouch (jam, misprint, torn film).
  const [spoiled, setSpoiled] = useState(0)
  const [spoiledSheet, setSpoiledSheet] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const onKey = useCallback((k: string) => {
    setErr(null)
    setValue((c) => typeKey(c, k, { decimals: 0, max: 9 }))
  }, [])
  const reading = value ? Number(value) : null
  const leftG = draft.leftover?.g ?? 0
  const per = run.sku.pouches_per_carton
  // More pouches than the sacks tipped in can fill: refused here, never sent (the server would hold it).
  const fit = packOverfill(
    unknownInputWeights(run) > 0 ? null : inputGrams(run),
    draft.count,
    run.sku.net_g,
    leftG,
    0,
    state.steps.get('PACK')?.loss_limit_pct,
  )

  const close = async () => {
    if (reading === null || fit.over) return
    setSaving(true)
    setErr(null)
    try {
      const lineId = run.equipment_id
      const prev = await getDraft<{ reading: number }>(counterDraftId(lineId))
      const previous = prev?.data.reading ?? null
      const codes = codesPrinted(reading, previous, draft.count + spoiled)
      const payload: PackClosePayload = {
        run_id: run.id,
        pack_lot_id: lot.id,
        pouches_good: draft.count,
        pouches_rejected: 0,
        labels_printed: codes.printed,
        labels_spoiled: spoiled,
        labels_destroyed: 0,
        // V1 has no last-pouch check (alignment map F7.4 switch): not confirmed, not refused.
        last_pouch_label_ok: false,
        counter_photo_id: null,
        counter_reading: reading,
        ...(previous !== null && previous <= reading ? { counter_previous: previous } : {}),
        phone_totals: {
          input_g: inputGrams(run),
          leftover_g: leftG,
          spillage_g: 0,
          reconciliation_gap: codes.gap ?? 0,
        },
      }
      // The close and the line's new reading are written together: a crash between them would
      // leave an old reading behind and the next close would count codes twice.
      await enqueueWithDraft(
        {
          type: 'pack_close',
          payload,
          actorId: actor.user_id,
          parentIds: runParentIds(run),
        },
        {
          id: counterDraftId(lineId),
          kind: 'pack.v1.counter',
          actor_id: actor.user_id,
          session_id: null,
          data: { reading, run_id: run.id },
        },
      )
      update((d) => ({
        ...d,
        closed: { pouches: d.count, leftover_g: leftG, reading, lot_code: lot.code },
      }))
    } catch (e) {
      setErr(saveFailed(e))
      setSheet(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <V1Screen
      main={{ padding: '8px 12px', gap: 8 }}
      header={
        <PackHeader
          onBack={() => go('leftover')}
          lot={head}
          icon="printer"
          title={tr('pack.v1.counter.title')}
          step={5}
        />
      }
      footer={
        <Footer dense>
          {err && <Refused text={err} />}
          {fit.over ? (
            <>
              <Refused
                text={tr('pack.v1.close.overfill', {
                  out: kg(fit.out_g),
                  in: kg(inputGrams(run)),
                })}
              />
              <WaitCta onClick={() => go('fill')}>{tr('pack.v1.close.fixCount')}</WaitCta>
            </>
          ) : reading !== null ? (
            <Cta
              label={tr('pack.v1.close.cta')}
              icon={ki('stamp')}
              onClick={() => setSheet(true)}
            />
          ) : (
            <WaitCta>{tr('pack.v1.counter.wait')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        spoiledSheet ? (
          <SpoiledSheet
            value={spoiled}
            onClose={() => setSpoiledSheet(false)}
            onSave={(n) => {
              setSpoiled(n)
              setSpoiledSheet(false)
            }}
          />
        ) : sheet && reading !== null && !fit.over ? (
          <YesSheet
            title={tr('pack.v1.close.title')}
            yes={tr('pack.v1.close.yes')}
            yesIcon="stamp"
            busy={saving}
            onYes={() => void close()}
            onClose={() => setSheet(false)}
            top={200}
          >
            <ItemRow
              padding="8px 10px"
              lead={<Lead icon="groundnut" />}
              title={tr('pack.v1.lotCode', { code: lot.code })}
              line={`${productName(run)} · ${packSize(run.sku.net_g)}`}
            />
            <IconRows
              rows={[
                { icon: 'pouch', label: tr('pack.v1.fill.filled'), value: String(draft.count) },
                {
                  icon: 'leftover',
                  label: tr('pack.v1.run.job4'),
                  value:
                    leftG > 0 ? tr('process.v1.kg', { kg: kg(leftG) }) : tr('pack.v1.left.none'),
                },
                {
                  icon: 'printer',
                  label: tr('pack.v1.counter.label'),
                  value: reading.toLocaleString('en-IN'),
                },
                {
                  icon: 'printer',
                  label: tr('pack.v1.counter.spoiled'),
                  value: String(spoiled),
                },
              ]}
            />
            <Locked>{tr('pack.v1.close.locked')}</Locked>
          </YesSheet>
        ) : undefined
      }
    >
      <SavedLine>
        {leftG > 0 ? tr('pack.v1.left.saved', { kg: kg(leftG) }) : tr('pack.v1.left.noneSaved')}
      </SavedLine>
      <Readout
        label={tr('pack.v1.counter.ask')}
        value={value}
        size={44}
        padding="4px 12px 6px"
        labelSize={16}
      />
      <span style={{ fontSize: 15, color: C.muted }}>
        {tr('pack.v1.counter.pouches', {
          count: draft.count,
          cartons: Math.ceil(draft.count / Math.max(1, per)),
        })}
      </span>
      <ItemRow
        padding="6px 10px"
        lead={<Lead icon="printer" />}
        title={<span style={{ fontSize: 17 }}>{tr('pack.v1.counter.spoiled')}</span>}
        line={tr('pack.v1.counter.spoiledHint')}
        right={
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <b style={{ fontSize: 20 }}>{spoiled}</b>
            <Btn icon="pencil" onClick={() => setSpoiledSheet(true)}>
              {tr('gate.v1.change')}
            </Btn>
          </div>
        }
      />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Screen>
  )
}

/** Codes printed but not on a good pouch (jammed, misprinted, torn): counted so they are no gap. */
function SpoiledSheet({
  value,
  onClose,
  onSave,
}: {
  value: number
  onClose: () => void
  onSave: (n: number) => void
}) {
  const [v, setV] = useState(value > 0 ? String(value) : '')
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 0, max: 5 })), [])
  return (
    <V1Sheet
      title={tr('pack.v1.counter.spoiled')}
      onClose={onClose}
      top={120}
      actions={
        <Cta label={tr('gate.v1.save')} icon="ok" onClick={() => onSave(v ? Number(v) : 0)} />
      }
    >
      <Readout label={tr('pack.v1.counter.spoiledHint')} value={v || '0'} size={32} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}

/** V1-F7-04j — रन बंद. */
function Closed({ run, closed }: { run: PackRunView; closed: NonNullable<RunDraft['closed']> }) {
  const navigate = useNavigate()
  const per = Math.max(1, run.sku.pouches_per_carton)
  return (
    <DoneScreen
      title={tr('pack.v1.closed.title')}
      lines={[
        tr('pack.v1.lotCode', { code: closed.lot_code }),
        tr('pack.v1.closed.line', {
          pouches: closed.pouches,
          cartons: Math.ceil(closed.pouches / per),
        }),
      ]}
      todo={[tr('pack.v1.closed.s1'), tr('pack.v1.closed.s2')]}
      cta={
        <Cta
          label={tr('pack.v1.done')}
          icon="ok"
          onClick={() => navigate('/m/pack', { replace: true })}
        />
      }
    />
  )
}

// ---------------------------------------------------------------------------------------------
// F7-B1.d — रुकिए: बैच रोका (an auto hold reached the line)
// ---------------------------------------------------------------------------------------------

function StopRun({ state, run, update, head, lab }: RunProps & { lab: boolean }) {
  const [call, setCall] = useState(false)
  const steps = [tr('pack.v1.stop.s1'), tr('pack.v1.stop.s2'), tr('pack.v1.stop.s3')]
  const why = tr('pack.v1.stop.why', {
    tail: shortLot(run.batch_code),
    reason: tr(lab ? 'pack.v1.stop.lab' : 'pack.v1.stop.office'),
  })
  return (
    <V1Screen
      header={
        <PackHeader
          lot={head}
          icon="hourglass"
          iconBg={C.accDark}
          title={tr('pack.v1.stop.title')}
        />
      }
      footer={
        <Footer>
          <Row>
            <Btn icon="call" onClick={() => setCall(true)}>
              {tr('pack.v1.supervisor')}
            </Btn>
          </Row>
          <Cta
            label={tr('pack.v1.close.cta')}
            icon={ki('stamp')}
            onClick={() => update((d) => ({ ...d, stop_ack: true, keyed: true, stage: 'fill' }))}
          />
        </Footer>
      }
      overlay={
        call ? (
          <CallSheet
            state={state}
            title={tr('pack.v1.supervisor')}
            onClose={() => setCall(false)}
          />
        ) : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          textAlign: 'center',
          padding: '6px 0',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 100,
            height: 100,
            borderRadius: 50,
            background: C.warnWash,
            color: C.accDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="hourglass" size={56} sw={1.8} />
        </span>
        <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.2 }}>{tr('pack.v1.stop.big')}</h1>
        <span style={{ fontSize: 18 }}>{why}</span>
      </div>
      <section
        style={{
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Steps steps={steps} ring={28} />
      </section>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 17,
            fontWeight: 700,
            color: C.help,
          }}
        >
          <SpeakButton
            text={plain([tr('pack.v1.stop.big'), why, ...steps].join('। '))}
            size={44}
            label={tr('pack.v1.listen')}
          />
          {tr('pack.v1.listen')}
        </span>
      </div>
    </V1Screen>
  )
}
